/* eslint-disable consistent-return */
/* eslint-disable no-shadow */
import gql from 'graphql-tag';
import { apolloClient } from '~/plugins/apollo';
import {
  CORE_CHANGEOVER_FIELDS,
  CORE_DEVICE_FIELDS,
  CORE_EVENT_FIELDS,
  CORE_EVENT_TYPE_FIELDS,
  CORE_MACHINE_COUNT_TYPE_FIELDS,
  CORE_MACHINE_FIELDS,
  EVENT_TYPE_FILTER_FIELDS,
} from '~/graphql/fragments';
import { DELETE_EVENT_TYPE } from '~/graphql/mutations/eventTypes';
import { UPDATE_EVENT } from '~/graphql/mutations/events';
import UPDATE_CHANGEOVER, { DISABLE_CHANGEOVER_COUNTING } from '~/graphql/mutations/changeover';

import { getEventTypeFriendlyName } from '~/helpers/event';

const deletionType = {
  code: 'DEL',
  names: {
    en: 'Event to be deleted',
    fr: 'Evénement à supprimer',
    de: 'Zu löschendes Ereignis',
    es: 'Evento a eliminar',
    it: 'Evento da eliminare',
    pl: 'Zdarzenie do usunięcia',
    zh: '事件删除',
  },
  color: '#63676B',
  external: 0,
  active: 0,
  notification: 0,
  customizable: 0,
  category: 0,
  id: '-5000',
};

export const state = {
  flatEventTypes: [],
  flatEventTypesWithTrashed: [],
  eventToCategorize: null,
  categorizationPopupOpen: false,
  preventCategorizationPopupToClose: false,
};

// getters
export const getters = {
  // Add an event to be able to print an event to delete
  getFlatEventTypes: (state) => state.flatEventTypes.concat(deletionType),
  getEventType: (state, getters) => (eventTypeId) => getters.getFlatEventTypes.find((t) => t.id === `${eventTypeId}`),
  getFlatEventTypeWithTrashed: (state) => (eventTypeId) => state.flatEventTypesWithTrashed.concat(deletionType).find((t) => t.id === `${eventTypeId}`),
  /**
   * Returns the friendly name of an event type
   */
  getEventTypeName: (state, getters) => (eventTypeId, locale) => {
    const foundType = getters.getEventType(eventTypeId);
    return getEventTypeFriendlyName(foundType, locale);
  },
  /**
   * Returns the friendly name of an event type with flatEventTypesWithTrashed
   */
  getFlatEventTypeWithTrashedName: (state, getters) => (eventTypeId, locale) => {
    const foundType = getters.getFlatEventTypeWithTrashed(eventTypeId);
    return getEventTypeFriendlyName(foundType, locale);
  },

  // Categorization popup
  getCategorizationPopupStatus: (state) => state.categorizationPopupOpen,

  // Categorization event for automatic popup up.
  getEventToCategorize: (state) => state.eventToCategorize,

  // Categorization event prevent flag.
  getPreventCategorizationPopupFlag: (state) => state.preventCategorizationPopupToClose,
};

// mutations
export const mutations = {
  commitFlatEventTypes(state, data) {
    const flatEventTypes = data.filter((event) => !event.deletedAt);
    state.flatEventTypesWithTrashed = data;
    state.flatEventTypes = flatEventTypes;
  },
  // Categorization popup status
  commitPopupToCategorize(state, data) {
    state.categorizationPopupOpen = data;
  },

  // Automactic categorization event.
  commitEventToCategorize(state, data) {
    state.eventToCategorize = data;
    state.preventCategorizationPopupToClose = false;
  },

  // Prevent to close the categorisation popup.
  commitPreventCategorizationPopupToClose(state) {
    state.preventCategorizationPopupToClose = true;
  },
};

// actions
export const actions = {
  /**
   * Fetch the event types from API (in a flat way)
   * @param context
   */
  async fetchFlatEventTypes(context) {
    const { data } = await apolloClient.query({
      query: gql`
        query EventTypes {
          eventTypes(first: 600, trashed: WITH) {
            data {
              ...CoreEventTypeFields
              ...EventTypeFilterFields
            }
          }
        }
        ${CORE_EVENT_TYPE_FIELDS}
        ${EVENT_TYPE_FILTER_FIELDS}
      `,
    });

    // Check if the data is defined and if the eventTypes.data is defined
    if (data?.eventTypes?.data) {
      context.commit('commitFlatEventTypes', data.eventTypes.data);

      return context.state.flatEventTypes;
    }
    return [];
  },
  /**
   * Updates a changeover associated to an event from API
   * @param context
   * @param payload
   */
  async putChangeover(context, payload) {
    let errors = {};
    const data = await apolloClient
      .mutate(
        {
          mutation: UPDATE_CHANGEOVER,
          variables: payload,
        },
      )
      .catch((error) => {
        const { graphQLErrors } = error;
        errors = graphQLErrors;
      });

    if (data !== undefined && data.data) {
      return data.data.updateChangeover;
    }
    if (data === undefined) {
      return errors;
    }
  },
  /**
   * Turn the automatic counting off for a changeover and its machine.
   * @param context
   * @param {string} id - changeover id
   * @returns {Promise<object>} the updated changeover
   */
  async disableChangeoverCounting(context, id) {
    const { data } = await apolloClient.mutate({
      mutation: DISABLE_CHANGEOVER_COUNTING,
      variables: { id },
    });
    return data.disableChangeoverCounting;
  },
  /**
   * Fetch an event from API.
   * You can choose to eventually retrieve a changeover
   * @param context
   * @param payload
   * @returns {Promise<*>}
   */
  async fetchEvent(context, payload) {
    const { id, eventType } = payload;
    const retrieveChangeover = !!payload.retrieveChangeover;
    return (await apolloClient.query(
      {
        query: gql`
          query GetSingleEvent ($id: ID!, $eventType: Int!, $retrieveChangeover: Boolean!) {
            event(id: $id, eventType: $eventType) {
              ...CoreEventFields,
              typeExpand {
                code
                color
                id
              }
              machineExpand {
                ...CoreMachineFields
                machine_devices : devicesExpand {
                  ...CoreDeviceFields
                }
                machine_count_type: countTypeExpand {
                  ...CoreMachineCountTypeFields
                }
              }
              changeoverExpand @include(if: $retrieveChangeover) {
                ...CoreChangeoverFields
                changeover_command_launches: commandLaunches
                is_last: isLast
                phaseExpand {
                  id
                  machine
                  number
                  quantity
                  status
                  unitTime
                  preparationTime
                }
                countTypeExpand {
                  id
                  name
                }
              }
            }
          }
          ${CORE_DEVICE_FIELDS}
          ${CORE_EVENT_FIELDS}
          ${CORE_MACHINE_FIELDS}
          ${CORE_MACHINE_COUNT_TYPE_FIELDS}
          ${CORE_CHANGEOVER_FIELDS}
        `,
        variables: {
          id,
          eventType: parseInt(eventType, 10),
          retrieveChangeover,
        },
      },
    )).data.event;
  },
  async putEvent(context, payload) {
    let errors = {};
    const data = await apolloClient
      .mutate(
        {
          mutation: UPDATE_EVENT,
          variables: payload,
        },
      )
      .catch((error) => {
        const { graphQLErrors } = error;
        errors = graphQLErrors;
      });

    if (data !== undefined && data.data) {
      return data.data.updateEvent;
    }
    if (data === undefined) {
      return errors;
    }
  },
  /**
   * Deletes FlatEventTypes from API
   * @param context
   * @param payload
   */
  async deleteFlatEventTypes(context, payload) {
    await apolloClient.mutate({
      // Query
      mutation: DELETE_EVENT_TYPE,
      // Parameters
      variables: payload,
    });
    context.commit('commitFlatEventTypes', context.state.flatEventTypes.map((event) => (event.id === payload.id ? { ...event, deletedAt: new Date() } : event)));
  },

};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
